import { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { getMenuDto } from '../services/restaurantDataService';
import { useItemsStore, useOrdersStore } from '../store';
import { mapDTOToDomain } from '../mappers/menu.mappers';
import type { MenuDTO } from '../dto/menu.dto';

export const useMenuData = (
  restaurantId: string | undefined,
  assetsVersionIso: string | null,
  selectedMode: 'LOCAL' | 'DELIVERY'
): { assetsLoading: boolean; menuError: string | null; menuPending: boolean } => {
  const setMenuStore = useItemsStore((s) => s.setMenu);
  const setMenuMeta = useItemsStore((s) => s.setMenuMeta);
  const setMenuUpdatedWarning = useItemsStore((s) => s.setMenuUpdatedWarning);
  const revalidatePrices = useOrdersStore((s) => s.revalidatePrices);

  const [assetsLoading, setAssetsLoading] = useState(false);
  const [menuDto, setMenuDto] = useState<MenuDTO | null>(null);
  const [menuError, setMenuError] = useState<string | null>(null);
  const reqId = useRef(0);

  useEffect(() => {
    setMenuDto(null);
    setMenuError(null);
    if (!restaurantId || !assetsVersionIso) return;

    const controller = new AbortController();
    const id = ++reqId.current;
    setAssetsLoading(true);

    getMenuDto(restaurantId, assetsVersionIso, selectedMode, controller.signal)
      .then((dto) => {
        if (controller.signal.aborted || id !== reqId.current) return;
        const categories = mapDTOToDomain(dto, selectedMode);
        setMenuStore({ categories });
        setMenuMeta(restaurantId, selectedMode, dto.metadata.last_updated_iso);
        const pricesChanged = revalidatePrices(categories);
        if (pricesChanged) setMenuUpdatedWarning(true);
        setMenuDto(dto);
        setMenuError(null);
      })
      .catch((err) => {
        if (controller.signal.aborted || id !== reqId.current) return;
        setMenuStore({ categories: [] });
        if (err?.name !== 'AbortError') {
          Sentry.captureException(err);
          setMenuError('Menú no disponible');
          setMenuDto(null);
        }
      })
      .finally(() => {
        if (id === reqId.current) setAssetsLoading(false);
      });

    return () => controller.abort();
  }, [restaurantId, assetsVersionIso, selectedMode, setMenuStore, setMenuMeta, setMenuUpdatedWarning, revalidatePrices]);

  const menuPending = !!restaurantId && !!assetsVersionIso && menuDto === null && !menuError;

  return { assetsLoading, menuError, menuPending };
};
